import { createContext, useContext } from 'react';

// Single source of truth for every screen name in the app — Screen (the type
// used everywhere) is DERIVED from this array rather than declared
// separately, so anything that needs the full list at runtime (e.g.
// web-routes.ts's getScreenFromPath fallback, which validates an arbitrary
// URL path segment against "is this actually a real screen") can import
// SCREEN_VALUES instead of hand-maintaining its own parallel copy that's
// guaranteed to drift.
export const SCREEN_VALUES = [
  'ai-setup', 'ai-usage',
  'welcome', 'login', 'unified-login', 'business-picker', 'business-creation-wizard', 'plan-selection', 'billing-return', 'billing-addons', 'channel-onboarding-stepper', 'register', 'otp-verification', 'forgot-password', 'reset-password', 'business-setup', 'home', 'dashboard', 'social', 'performance', 'whatsapp-test', 'whatsapp-business-setup', 'whatsapp-conversation', 'send-whatsapp-message', 'contacts', 'contact-create', 'bulk-contact-upload', 'facebook-messenger-setup', 'facebook-callback', 'x-twitter-setup', 'phone-setup', 'call-history', 'chat', 'conversation', 'subscription', 'user-setup', 'departments', 'department-add', 'department-edit', 'invitation-acceptance', 'notifications', 'payment', 'task-history', 'settings', 'products', 'products-list', 'product-detail', 'product-create', 'product-edit', 'product-bulk-import', 'catalogues', 'catalogue-detail', 'catalogue-create', 'catalogue-edit', 'add-products-to-catalogue', 'manage-catalogue-channels', 'quick-replies', 'quick-reply-items', 'quick-reply-set-detail', 'quick-reply-set-create', 'quick-reply-set-edit', 'quick-reply-set-items', 'quick-reply-item-create', 'quick-reply-item-edit', 'dispatch-rules-list', 'dispatch-rule-edit', 'flows-list', 'flow-edit', 'calendars', 'calendar-create', 'calendar-edit', 'order-success', 'calendar-events', 'event-create', 'event-edit', 'event-detail', 'business-calendar', 'calendar-booking', 'product-view', 'catalog-view', 'cart', 'checkout', 'orders', 'order-add', 'order-edit', 'product-scanner', 'devices', 'device-create', 'device-edit', 'units', 'shipping-methods', 'shipping-method-create', 'shipping-method-edit', 'inventory', 'business-config', 'business-profile', 'coupons', 'referrals', 'track-order', 'template-management', 'dashboard-team-all', 'dashboard-templates-all', 'dashboard-flows-all', 'dashboard-assignments-all', 'posts-list', 'post-create', 'data-lists', 'lead-finder', 'lead-run', 'assignment-rules', 'assignment-rule-edit', 'data-list-schema-create', 'data-list-schema-edit', 'data-list-records', 'data-list-record-create', 'data-list-record-edit', 'business-permissions', 'add-verify-identity', 'visits-setup', 'visits', 'visit-detail', 'visit-create', 'visit-execute', 'visit-route', 'visits-campaigns', 'visit-campaign-create', 'visit-campaign-detail', 'visit-campaign-distribute',
  'vendors', 'vendor-create', 'vendor-edit', 'vendor-detail', 'vendor-add-products', 'vendor-bulk-upload',
  'quotations', 'quotation-create', 'quotation-edit', 'quotation-detail', 'quotation-templates', 'quotation-template-edit',
] as const;

export type Screen = (typeof SCREEN_VALUES)[number];

// Create context for shared app state and methods
export interface AppContextType {
  currentScreen: Screen;
  navigateTo: (screen: Screen, param?: string, routeParams?: Record<string, any>) => void;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning' | 'message', title: string, message: string) => void;
  showClickableNotification: (type: 'success' | 'error' | 'info' | 'warning' | 'message', title: string, message: string, action?: { label: string; onPress: () => void; channel?: string; channelName?: string }) => void;
  // Navigation methods only
  handleGetStarted: () => void;
  handleSignUp: () => void;
  handleBackToLogin: () => void;
  handleForgotPassword: () => void;
  handleBackToWelcome: () => void;
  handleBackToRegister: () => void;
  handleLogout: () => void;
  handleWhatsAppTest: () => void;
  handleChatNavigation: () => void;
  handleConversationNavigation: (conversationId: string) => void;
  // Socket room management
  joinConversationRooms: (conversationList?: Array<{ id?: string; conversationId?: string }>) => Promise<void>;
  // Social login
  handleSocialLogin: (provider: 'google' | 'facebook' | 'apple') => void;
  // Shared state
  pendingEmail: string;
  setPendingEmail: (email: string) => void;
  isNewUser: boolean;
  setIsNewUser: (isNew: boolean) => void;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  routeParams?: Record<string, any>; // Route parameters from URL or navigation
  // Phone channel — headless InnoCalls WebRTC dialer (web only for now).
  // Screens call startPhoneCall(number, contactId, displayInfo) to place a
  // call; the dialer's own widget is never shown — only phoneCallState/
  // phoneCallDuration/phoneCallContact, rendered by the app's own call screen
  // (see PhoneCallScreen, hosted inside RightPane).
  phoneCallState: 'idle' | 'calling' | 'ringing' | 'connected' | 'completed' | 'not-responded' | 'cancelled' | 'ended' | 'failed';
  phoneCallDuration: number;
  phoneCallError: string | null;
  phoneCallContact: { name: string; avatar?: string; linkedChannels?: string[]; contactId?: string; phoneNumber?: string; dialTarget?: string } | null;
  phoneCallMinimized: boolean;
  setPhoneCallMinimized: (minimized: boolean) => void;
  isPhoneDialerReady: boolean;
  startPhoneCall: (phoneNumber: string, contactId?: string, displayInfo?: { name: string; avatar?: string; linkedChannels?: string[] }) => void;
  hangupPhoneCall: () => void;
  /** Explicitly dismisses a terminal call state (cancelled/not-responded/failed) —
   * those states persist on screen until the user acts, rather than
   * auto-clearing, so a missed/unanswered call doesn't just disappear. */
  dismissPhoneCall: () => void;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

// Hook to use app context
export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within AppProvider');
  }
  return context;
};
